import React, { useEffect, useState } from 'react';
import { BookOpen, HeartCrack, Flower2, Sparkles, Sparkle, Flower, Gem, BookMarked, Headphones, Heart, Flame, MessageCircle } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { BonusCard } from './BonusCard';
import { TestimonialCard } from './TestimonialCard';
import { FAQAccordion } from './FAQAccordion';
import { SalesNotification } from './SalesNotification';
import { HeroSection } from './HeroSection';
export function LandingPage() {
  // Function to get exact current time in New York timezone
  const getNYCTime = (): Date => {
    const now = new Date();
    const nycTimeString = now.toLocaleString('en-US', {
      timeZone: 'America/New_York'
    });
    return new Date(nycTimeString);
  };
  // Function to get next 8:35 PM NYC time
  const getNextTarget = (): number => {
    const nycNow = getNYCTime();
    const targetDate = new Date(nycNow);
    // Set to 8:35 PM (20:35)
    targetDate.setHours(20, 35, 0, 0);
    // If current time is past 8:35 PM today, set to tomorrow 8:35 PM
    if (nycNow.getTime() >= targetDate.getTime()) {
      targetDate.setDate(targetDate.getDate() + 1);
    }
    return targetDate.getTime();
  };
  const [targetTs, setTargetTs] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<string>('00:00:00');
  // Initialize target timestamp (next 8:35 PM in NYC)
  useEffect(() => {
    const target = getNextTarget();
    setTargetTs(target);
  }, []);
  // Update countdown every second
  useEffect(() => {
    if (targetTs === 0) return;
    const updateCountdown = () => {
      const nycNow = getNYCTime();
      const diff = targetTs - nycNow.getTime();
      if (diff <= 0) {
        // Reset to next 8:35 PM
        const newTarget = getNextTarget();
        setTargetTs(newTarget);
        setTimeRemaining('00:00:00');
        return;
      }
      // Calculate hours, minutes, seconds
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(diff % (1000 * 60) / 1000);
      // Format with leading zeros
      const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      setTimeRemaining(formatted);
    };
    // Initial update
    updateCountdown();
    // Update every second
    const interval = setInterval(updateCountdown, 1000);
    // Cleanup
    return () => clearInterval(interval);
  }, [targetTs]);
  const handleCTAClick = () => {
    console.log('CTA clicked - integrate with payment system');
  };
  const handleBonusDownload = () => {
    window.location.href = 'https://geybw4-da.myshopify.com/checkouts/cn/hWN5lXmD1205VOnSw9lHPrMo/en-us?_r=AQABPdB74FQlZpWhkK6Sz1JaCCS2zP05DEprvCGGxPZDEdI&auto_redirect=false&edge_redirect=true&preview_theme_id=135434305587&skip_shop_pay=true';
  };
  const scrollToPrice = () => {
    const priceSection = document.getElementById('price-section');
    if (priceSection) {
      priceSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  const faqItems = [{
    question: 'Does this book replace therapy?',
    answer: 'No. It is an emotional and spiritual complement to any therapeutic process.'
  }, {
    question: 'How do I receive the bonuses?',
    answer: 'Automatically with your digital download.'
  }, {
    question: 'What format are the book and bonuses?',
    answer: 'PDF format, compatible with mobile, tablet, or laptop.'
  }, {
    question: 'Can I gift it to someone?',
    answer: 'Yes. You will receive a link that you can share with whoever needs it.'
  }, {
    question: 'How long will the offer be available?',
    answer: 'For a limited time. When the launch ends, the bonuses will return to their original price.'
  }];
  return <div className="w-full min-h-screen bg-[#F6EFE8]" data-id="element-86">
      {/* Sales Notification - Mobile Only */}
      <SalesNotification data-id="element-87" />

      {/* Header Bar - Soft and elegant */}
      <div className="bg-[#1C2A3A] text-[#FBF9F6] py-3 px-4 text-center sticky top-0 z-50 shadow-soft" data-id="element-88">
        <p className="text-sm md:text-base font-medium flex items-center justify-center gap-2" data-id="element-89">
          <BookOpen className="w-5 h-5" strokeWidth={2} data-id="element-90" />
          <span className="font-semibold" data-id="element-91">LAUNCH OFFER:</span> $24.99 + 3
          PREMIUM BONUSES — Limited time only!
        </p>
      </div>

      {/* Hero Section with Custom Audio Player */}
      <HeroSection onCTAClick={handleCTAClick} audioUrl="https://files.catbox.moe/ii497t.m4a" data-id="element-92" />

      {/* Emotional Connection Section */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-id="element-93">
        <div className="space-y-8 text-left" data-id="element-94">
          <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-95">
            I know, beautiful. You've tried everything: meditating, breathing,
            repeating positive phrases, even smiling when inside you're tired of
            holding yourself together.
          </p>
          <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-96">
            Sometimes you feel like you're getting better... but suddenly, it
            comes back: the anguish, the fear, the guilt.
          </p>
          <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-97">
            And you wonder why, if you do so much to be well, you still feel so
            bad.
          </p>

          <div className="bg-[#FBF9F6] rounded-2xl p-8 shadow-soft-lg border-l-4 border-[#C48A47] my-12" data-id="element-98">
            <div className="flex items-start gap-4 mb-4" data-id="element-99">
              <HeartCrack className="w-8 h-8 text-[#C48A47] flex-shrink-0" strokeWidth={1.5} data-id="element-100" />
              <p className="text-2xl font-['Crimson_Text'] font-semibold text-[#1C2A3A]" data-id="element-101">
                You're not broken.
              </p>
            </div>
            <p className="text-lg text-[#3E4A55]" data-id="element-102">
              You're feeling more than the world taught you to hide.
            </p>
          </div>

          <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-103">
            We were told that anxiety is an enemy. That crying is weakness. That
            feeling too much is &quot;being too much&quot;.
          </p>
          <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-104">
            But the truth is different: your anxiety isn't against you. It's
            with you.
          </p>
          <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-105">
            It's your soul crying out: &quot;Look at me, I need to heal!&quot;
          </p>

          <div className="text-center my-12" data-id="element-106">
            <div className="flex items-center justify-center gap-3 mb-2" data-id="element-107">
              <Flower2 className="w-8 h-8 text-[#E8905C]" strokeWidth={1.5} data-id="element-108" />
              <p className="text-2xl font-['Crimson_Text'] font-semibold text-[#E8905C]" data-id="element-109">
                And when you finally listen to it, everything changes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-110" />

      {/* Problem & Solution Section */}
      <section className="bg-[#FBF9F6] py-20" data-id="element-111">
        <div className="max-w-6xl mx-auto px-6" data-id="element-112">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-113">
            <div data-id="element-114">
              <h2 className="font-['Crimson_Text'] text-3xl md:text-4xl font-semibold text-[#1C2A3A] mb-8" data-id="element-115">
                &quot;Embracing My Anxiety&quot; is not another self-help book.
              </h2>
              <p className="text-xl text-[#3E4A55] mb-8 leading-relaxed" data-id="element-116">
                It's an encounter with the deepest part of you.
              </p>

              <div className="space-y-6" data-id="element-117">
                <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-118">
                  A guide written with soul, so you can stop fighting against
                  your mind and start reconciling with your heart.
                </p>
                <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-119">
                  Each story and exercise was created to help you transform your
                  fear into understanding, your guilt into tenderness, your
                  anxiety into a path.
                </p>
              </div>
            </div>
            <div className="relative" data-id="element-120">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/cNxSKr6akHP8vjtyJxrhyB/WhatsApp_Image_2025-11-29_at_2.08.16_AM.jpg" alt="Embracing My Anxiety book displayed with candles in a peaceful setting" className="w-full h-auto rounded-2xl shadow-soft-xl" data-id="element-121" />
            </div>
          </div>

          <div className="bg-[#E8DAC7] rounded-2xl p-8 mb-12 max-w-4xl mx-auto shadow-soft" data-id="element-122">
            <h3 className="font-['Crimson_Text'] text-2xl font-semibold text-[#1C2A3A] mb-6" data-id="element-123">
              If you identify with this, this book is for you:
            </h3>
            <ul className="space-y-4" data-id="element-124">
              {['You feel like your mind never shuts off, not even for a second.', 'You fear your own emotions.', 'You live on alert even when everything seems fine.', 'You struggle to enjoy the present because your mind always goes to "what if...?"', 'You feel like no one understands you, even when surrounded by people.'].map((item, index) => <li key={index} className="flex items-start" data-id="element-125">
                  <span className="text-[#E8905C] mr-3 text-xl" data-id="element-126">•</span>
                  <span className="text-[#3E4A55]" data-id="element-127">{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="flex justify-center my-16" data-id="element-128">
            <button onClick={scrollToPrice} className="w-full md:w-auto px-10 py-5 bg-[#d8953d] hover:bg-[#c17d2a] text-white text-lg font-bold rounded-2xl shadow-soft-lg hover:shadow-soft-xl transition-all duration-300 active:scale-95" data-id="element-129">
              Download the eBook now for $24.99
            </button>
          </div>

          <div className="text-center bg-gradient-to-r from-[#E8905C]/10 to-[#C48A47]/10 rounded-2xl p-10 max-w-4xl mx-auto shadow-soft" data-id="element-130">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-131">
              <Sparkles className="w-8 h-8 text-[#E8905C]" strokeWidth={1.5} data-id="element-132" />
              <p className="text-2xl font-['Crimson_Text'] font-semibold text-[#1C2A3A]" data-id="element-133">
                This book will remind you of something essential:
              </p>
            </div>
            <p className="text-3xl font-['Crimson_Text'] font-bold text-[#E8905C] mt-4" data-id="element-134">
              there's nothing broken in you.
            </p>
            <p className="text-xl text-[#3E4A55] mt-2" data-id="element-135">
              Just a part of you asking to be embraced.
            </p>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-136" />

      {/* What's Inside Section */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-id="element-137">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-138">
          <div className="order-2 md:order-1 relative" data-id="element-139">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jDcFThWjKFuVYBppQJzDEo/ChatGPT_Image_Nov_29%2C_2025%2C_02_07_11_AM.png" alt="Woman reading Embracing My Anxiety in a comfortable setting" className="w-full h-auto rounded-2xl shadow-soft-xl" data-id="element-140" />
          </div>
          <div className="order-1 md:order-2" data-id="element-141">
            <h2 className="font-['Crimson_Text'] text-3xl md:text-4xl font-semibold text-[#1C2A3A] mb-6" data-id="element-142">
              Imagine waking up without fear of feeling
            </h2>
            <p className="text-lg text-[#3E4A55] leading-relaxed mb-4" data-id="element-143">
              Breathing without that pressure in your chest.
            </p>
            <p className="text-lg text-[#3E4A55] leading-relaxed mb-4" data-id="element-144">
              Walking through your day without thinking every thought is
              dangerous.
            </p>
            <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-145">
              Looking in the mirror and seeing not a weak woman, but a brave
              woman for feeling.
            </p>
          </div>
        </div>

        <div className="bg-[#FBF9F6] rounded-2xl p-10 shadow-soft-lg mb-16 max-w-4xl mx-auto" data-id="element-146">
          <p className="text-xl text-[#1C2A3A] mb-6 leading-relaxed" data-id="element-147">
            That's what{' '}
            <span className="font-semibold" data-id="element-148">Embracing My Anxiety</span> teaches
            you:
          </p>
          <p className="text-2xl font-['Crimson_Text'] font-semibold text-[#E8905C] text-center" data-id="element-149">
            to transform every wave of fear into a wave of love.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-150">
          <div className="bg-[#FBF9F6] rounded-2xl p-6 shadow-soft text-center" data-id="element-151">
            <p className="text-[#3E4A55] leading-relaxed" data-id="element-152">
              Your anxiety is a scared child: if you ignore her, she screams; if
              you embrace her, she calms down.
            </p>
          </div>
          <div className="bg-[#FBF9F6] rounded-2xl p-6 shadow-soft text-center" data-id="element-153">
            <p className="text-[#3E4A55] leading-relaxed" data-id="element-154">
              Your soul is an ocean: the waves can be intense, but at the bottom
              there's always calm.
            </p>
          </div>
          <div className="bg-[#FBF9F6] rounded-2xl p-6 shadow-soft text-center" data-id="element-155">
            <p className="text-[#3E4A55] leading-relaxed" data-id="element-156">
              Your anxiety is a lighthouse: it doesn't punish you, it guides you
              to where you need to look.
            </p>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-157" />

      {/* Author Story Section */}
      <section className="bg-[#FBF9F6] py-20" data-id="element-158">
        <div className="max-w-5xl mx-auto px-6" data-id="element-159">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-160">
            <div className="relative" data-id="element-161"></div>
            <div className="bg-[#E8DAC7] rounded-2xl p-10 shadow-soft-lg border-t-4 border-[#C48A47]" data-id="element-162">
              <p className="text-lg text-[#3E4A55] leading-relaxed mb-6" data-id="element-163">
                One night, the author felt like they couldn't take it anymore.
              </p>
              <p className="text-lg text-[#3E4A55] leading-relaxed mb-6" data-id="element-164">
                Racing heart, trembling hands, total fear.
              </p>
              <p className="text-lg text-[#3E4A55] leading-relaxed mb-6" data-id="element-165">
                They thought there was no way out...
              </p>
              <p className="text-xl font-['Crimson_Text'] font-semibold text-[#E8905C] mb-6" data-id="element-166">
                Until they understood that the way out wasn't outward, but
                inward.
              </p>
              <p className="text-lg text-[#3E4A55] leading-relaxed mb-6" data-id="element-167">
                From that dark moment, this book was born.
              </p>
              <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-168">
                Each page was written from real experience, with tears,
                understanding, and the hope that you too can find your peace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-169" />

      {/* Offer & Bonuses Section - BEFORE TESTIMONIALS */}
      <section id="price-section" className="py-20" data-id="element-170">
        <div className="max-w-5xl mx-auto px-6" data-id="element-171">
          {/* Countdown Timer */}
          <div className="mb-12 text-center" data-id="element-172">
            <div className="bg-gradient-to-r from-[#C48A47] to-[#E8905C] rounded-2xl p-8 shadow-soft-xl inline-block" data-id="element-173">
              <p className="text-[#FBF9F6] text-sm font-semibold mb-3 tracking-wide uppercase" data-id="element-174">
                ⏰ Special Offer Ends In
              </p>
              <p className="text-white text-5xl md:text-6xl font-bold font-['Crimson_Text'] mb-2" data-id="element-175">
                {timeRemaining}
              </p>
              <p className="text-[#FBF9F6]/90 text-lg font-medium" data-id="element-176">remaining</p>
            </div>
          </div>

          <div className="text-center mb-16" data-id="element-177">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-178">
              <Heart className="w-10 h-10 text-[#C48A47]" strokeWidth={1.5} fill="#C48A47" data-id="element-179" />
              <h2 className="font-['Crimson_Text'] text-3xl md:text-5xl font-semibold text-[#1C2A3A]" data-id="element-180">
                SPECIAL LAUNCH OFFER
              </h2>
            </div>
            <p className="text-xl text-[#3E4A55] mb-8 leading-relaxed" data-id="element-181">
              For less than the cost of a therapy session, you'll have
              companionship that you can reread whenever you need to return to
              yourself.
            </p>
            <div className="bg-[#FBF9F6] rounded-2xl p-8 inline-block shadow-soft-xl" data-id="element-182">
              <div className="flex items-center justify-center gap-2 mb-2" data-id="element-183">
                <Heart className="w-6 h-6 text-[#1C2A3A]" strokeWidth={2} fill="#1C2A3A" data-id="element-184" />
                <p className="text-lg text-[#1C2A3A] font-semibold" data-id="element-185">
                  Special price:
                </p>
              </div>
              <p className="text-5xl font-['Crimson_Text'] font-bold text-[#1C2A3A] mb-4" data-id="element-186">
                $24.99 USD
              </p>
              <div className="flex items-center justify-center gap-2 mb-2" data-id="element-187">
                <Gem className="w-6 h-6 text-[#1C2A3A]" strokeWidth={2} data-id="element-188" />
                <p className="text-lg text-[#1C2A3A] font-semibold" data-id="element-189">
                  Includes 3 EXCLUSIVE BONUSES
                </p>
              </div>
              <p className="text-sm text-[#1C2A3A]" data-id="element-190">
                (Total value: $91.90 USD) — FREE TODAY
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-191">
            <BonusCard icon={Flower} title='BONUS #1 — "Rituals of Calm"' subtitle="8-10 page PDF guide" description="7 practices to stop anxiety and return to yourself. Not magic rituals, but reminders to return to the present." quote="What part of me needs compassion today?" value="Value: $29.97 — Today FREE" data-id="element-192" />
            <BonusCard icon={BookMarked} title='BONUS #2 — "The Soul Journal"' subtitle="21-day editable digital journal" description="21 days to transform your anxiety into self-compassion. Each day guides you with a phrase from the book, gentle questions, and a final affirmation." quote="Your anxiety isn't an enemy, it's a pending conversation with yourself." value="Value: $27.97 — Today FREE" data-id="element-193" />
            <BonusCard icon={Headphones} title='BONUS #3 — "Complete Audiobook of Embracing My Anxiety"' subtitle="Emotional companion audio" description="A version narrated in a warm voice that accompanies you when you need it most. Listen during your walks, before bed, or anytime your anxiety intensifies. This audiobook guides you back to your center with gentleness and understanding." quote="Breathe. You're not alone. Your calm always returns to you." value="🎧 Value: $33.96 — Today FREE" data-id="element-194" />
          </div>

          <div className="bg-[#FBF9F6] rounded-2xl p-10 shadow-soft-xl text-center" data-id="element-195">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-196">
              <Flame className="w-8 h-8 text-[#1C2A3A]" strokeWidth={1.5} data-id="element-197" />
              <p className="text-2xl font-['Crimson_Text'] font-semibold text-[#1C2A3A]" data-id="element-198">
                Total value: $91.90 USD
              </p>
            </div>
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-199">
              <Heart className="w-8 h-8 text-[#1C2A3A]" strokeWidth={1.5} fill="#1C2A3A" data-id="element-200" />
              <p className="text-3xl font-['Crimson_Text'] font-bold text-[#1C2A3A]" data-id="element-201">
                You pay only $24.99 USD
              </p>
            </div>
            <p className="text-sm text-[#3E4A55] mb-8" data-id="element-202">(limited time)</p>
            <CTAButton size="large" onClick={handleBonusDownload} data-id="element-203">
              Download now and claim my 3 free bonuses
            </CTAButton>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-204" />

      {/* Testimonials Section - MOVED AFTER BONUSES */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-id="element-205">
        <h2 className="font-['Crimson_Text'] text-3xl md:text-4xl font-semibold text-[#1C2A3A] mb-12 text-center" data-id="element-206">
          What people who embraced their anxiety say
        </h2>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-207">
          <TestimonialCard quote="I cried, I laughed, and finally felt peace. It's the first book that doesn't make me feel defective for having anxiety." author="Lucía G." image="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&q=80" rating={5} data-id="element-208" />
          <TestimonialCard quote="After years of therapy, I understood that I don't have to fight against myself." author="Andrea P." image="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=400&fit=crop&q=80" rating={5} data-id="element-209" />
          <TestimonialCard quote="For the first time, I understood what my anxiety was trying to tell me." author="María E." image="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&h=400&fit=crop&q=80" rating={4.5} data-id="element-210" />
          <TestimonialCard quote="This book arrived at the exact moment. Each page felt like a hug I needed." author="Carmen R." image="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=400&fit=crop&q=80" rating={5} data-id="element-211" />
          <TestimonialCard quote="I finally understood that my sensitivity isn't weakness, it's my superpower." author="Isabella M." image="https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=400&h=400&fit=crop&q=80" rating={5} data-id="element-212" />
          <TestimonialCard quote="I cried in every chapter, but they were tears of liberation, not pain." author="Sofia T." image="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop&q=80" rating={4.5} data-id="element-213" />
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-214" />

      {/* Guarantee Section */}
      <section className="max-w-3xl mx-auto px-6 py-20" data-id="element-215">
        <div className="bg-[#FBF9F6] rounded-2xl p-10 shadow-soft-xl border-l-4 border-[#E8905C]" data-id="element-216">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-217">
            <MessageCircle className="w-8 h-8 text-[#E8905C]" strokeWidth={1.5} data-id="element-218" />
            <h3 className="font-['Crimson_Text'] text-2xl font-semibold text-[#1C2A3A] text-center" data-id="element-219">
              Your peace of mind is guaranteed:
            </h3>
          </div>
          <p className="text-lg text-[#3E4A55] leading-relaxed text-center" data-id="element-220">
            If within 30 days you don't feel that this book helped you look at
            your anxiety with more understanding, we'll refund your money.
          </p>
          <p className="text-lg text-[#E8905C] font-semibold text-center mt-4" data-id="element-221">
            No questions. No guilt. Just love.
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-222" />

      {/* FAQ Section */}
      <section className="max-w-3xl mx-auto px-6 py-20" data-id="element-223">
        <h2 className="font-['Crimson_Text'] text-3xl md:text-4xl font-semibold text-[#1C2A3A] mb-12 text-center" data-id="element-224">
          Frequently Asked Questions
        </h2>
        <FAQAccordion items={faqItems} data-id="element-225" />
      </section>

      {/* Section Divider */}
      <div className="section-divider max-w-6xl mx-auto" data-id="element-226" />

      {/* Gift Idea Section */}
      <section className="bg-[#FBF9F6] py-20" data-id="element-227">
        <div className="max-w-5xl mx-auto px-6" data-id="element-228">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-229">
            <div data-id="element-230">
              <h2 className="font-['Crimson_Text'] text-3xl md:text-4xl font-semibold text-[#1C2A3A] mb-6" data-id="element-231">
                The perfect gift for someone you love
              </h2>
              <p className="text-lg text-[#3E4A55] leading-relaxed mb-6" data-id="element-232">
                If you know someone struggling with their anxiety, this book can
                be the embrace they need.
              </p>
              <p className="text-lg text-[#3E4A55] leading-relaxed" data-id="element-233">
                A gift that says: &quot;I see you, I understand you, and I'm
                here for you&quot;.
              </p>
            </div>
            <div className="relative" data-id="element-234">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8EcseXD89oD6aVRmorqBF7/Generated_Image_October_18%2C_2025_-_7_01PM.png" alt="Embracing My Anxiety as a thoughtful holiday gift" className="w-full h-auto rounded-2xl shadow-soft-xl" data-id="element-235" />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-gradient-to-b from-[#1C2A3A] to-[#3E4A55] py-20" data-id="element-236">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-237">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-238">
            <Flower2 className="w-10 h-10 text-[#FBF9F6]" strokeWidth={1.5} data-id="element-239" />
            <h2 className="font-['Crimson_Text'] text-3xl md:text-5xl font-semibold text-[#FBF9F6]" data-id="element-240">
              Don't wait for everything to be okay to start healing.
            </h2>
          </div>
          <p className="text-xl text-[#FBF9F6]/90 mb-6 leading-relaxed" data-id="element-241">
            Healing doesn't happen when everything is under control, but when
            you allow yourself to feel, even with fear.
          </p>
          <p className="text-xl text-[#FBF9F6]/90 mb-8 leading-relaxed" data-id="element-242">
            Your soul has been waiting for you for a long time.
          </p>
          <p className="text-xl text-[#FBF9F6] font-semibold mb-8" data-id="element-243">
            This book can be the key that opens that door.
          </p>
          <div className="bg-[#FBF9F6]/10 backdrop-blur-sm rounded-2xl p-8 mb-8" data-id="element-244">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-245">
              <Sparkles className="w-8 h-8 text-[#FBF9F6]" strokeWidth={1.5} data-id="element-246" />
              <p className="text-2xl font-['Crimson_Text'] font-semibold text-[#FBF9F6]" data-id="element-247">
                This isn't the end of your struggle. It's the beginning of your
                freedom.
              </p>
            </div>
          </div>
          <CTAButton variant="secondary" size="large" onClick={handleCTAClick} data-id="element-248">
            Yes, I want to embrace my anxiety and receive my 3 free bonuses
          </CTAButton>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C2A3A] text-[#FBF9F6]/70 py-8" data-id="element-249">
        <div className="max-w-5xl mx-auto px-6 text-center text-sm" data-id="element-250">
          <p className="mb-2" data-id="element-251">
            © 2025 Embracing My Anxiety. All rights reserved.
          </p>
          <p data-id="element-252">
            This book does not replace medical or psychological care. If you're
            going through an emotional crisis, seek professional support.
          </p>
        </div>
      </footer>
    </div>;
}